/**
 * Timeline Types
 * Types for client timeline events
 */

// Event types
export type TimelineEventType =
  | 'interaction_created'
  | 'interaction_completed'
  | 'interaction_cancelled'
  | 'interaction_converted_to_case'
  | 'interaction_converted_to_legal_process'
  | 'case_created'
  | 'case_created_from_interaction'
  | 'case_status_changed'
  | 'case_updated'
  | 'case_converted_to_legal'
  | 'case_closed'
  | 'case_archived'
  | 'legal_process_created'
  | 'legal_process_created_from_interaction'
  | 'legal_process_created_from_case'
  | 'legal_process_updated'
  | 'legal_process_status_changed'
  | 'legal_process_concluded'
  | 'legal_process_archived'
  | 'task_created'
  | 'task_completed'
  | 'task_overdue'
  | 'task_cancelled'
  | 'document_uploaded'
  | 'document_shared'
  | 'note_added'
  | 'payment_received'
  | 'invoice_sent'
  | 'invoice_paid'
  | 'deadline_approaching'
  | 'deadline_missed'
  | 'whatsapp_sent'
  | 'email_sent';

// Entity types
export type TimelineEntityType =
  | 'task'
  | 'interaction'
  | 'case'
  | 'legal_process'
  | 'document'
  | 'note'
  | 'payment'
  | 'invoice';

// Timeline event interface
export interface TimelineEvent {
  id: string;
  clientId: string;
  eventType: TimelineEventType;
  entityType: TimelineEntityType;
  entityId: string;
  title: string;
  description?: string;
  metadata?: Record<string, unknown>;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  priority: number;
}

// Timeline filters
export interface TimelineFilters {
  types?: TimelineEventType[];
  fromDate?: string;
  toDate?: string;
}

// Timeline query params
export interface TimelineQueryParams extends TimelineFilters {
  page?: number;
  limit?: number;
}

// Timeline response
export interface TimelineResponse {
  events: TimelineEvent[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Event type labels (UI)
export const TIMELINE_EVENT_TYPE_LABELS: Record<TimelineEventType, string> = {
  interaction_created: 'Atendimento criado',
  interaction_completed: 'Atendimento concluído',
  interaction_cancelled: 'Atendimento cancelado',
  interaction_converted_to_case: 'Atendimento convertido em Caso',
  interaction_converted_to_legal_process: 'Atendimento convertido em Processo Judicial',
  case_created: 'Caso criado',
  case_created_from_interaction: 'Caso criado (de Atendimento)',
  case_status_changed: 'Status do caso alterado',
  case_updated: 'Caso atualizado',
  case_converted_to_legal: 'Caso convertido em Processo Judicial',
  case_closed: 'Caso encerrado',
  case_archived: 'Caso arquivado',
  legal_process_created: 'Processo Judicial criado',
  legal_process_created_from_interaction: 'Processo criado (de Atendimento)',
  legal_process_created_from_case: 'Processo criado (de Caso)',
  legal_process_updated: 'Processo atualizado',
  legal_process_status_changed: 'Status do processo alterado',
  legal_process_concluded: 'Processo concluído',
  legal_process_archived: 'Processo arquivado',
  task_created: 'Tarefa criada',
  task_completed: 'Tarefa concluída',
  task_overdue: 'Tarefa atrasada',
  task_cancelled: 'Tarefa cancelada',
  document_uploaded: 'Documento enviado',
  document_shared: 'Documento compartilhado',
  note_added: 'Nota adicionada',
  payment_received: 'Pagamento recebido',
  invoice_sent: 'Fatura enviada',
  invoice_paid: 'Fatura paga',
  deadline_approaching: 'Prazo se aproximando',
  deadline_missed: 'Prazo perdido',
  whatsapp_sent: 'WhatsApp enviado',
  email_sent: 'Email enviado'
};

// Event type icons (UI)
export const TIMELINE_EVENT_TYPE_ICONS: Record<TimelineEventType, string> = {
  interaction_created: '📋',
  interaction_completed: '✅',
  interaction_cancelled: '❌',
  interaction_converted_to_case: '🔄',
  interaction_converted_to_legal_process: '⚖️',
  case_created: '📂',
  case_created_from_interaction: '📂',
  case_status_changed: '🔄',
  case_updated: '📝',
  case_converted_to_legal: '⚖️',
  case_closed: '🔒',
  case_archived: '📦',
  legal_process_created: '⚖️',
  legal_process_created_from_interaction: '⚖️',
  legal_process_created_from_case: '⚖️',
  legal_process_updated: '📝',
  legal_process_status_changed: '🔄',
  legal_process_concluded: '✅',
  legal_process_archived: '📦',
  task_created: '🔔',
  task_completed: '✓',
  task_overdue: '⚠️',
  task_cancelled: '🚫',
  document_uploaded: '📄',
  document_shared: '🔗',
  note_added: '📌',
  payment_received: '💰',
  invoice_sent: '📧',
  invoice_paid: '✅',
  deadline_approaching: '⏰',
  deadline_missed: '🔴',
  whatsapp_sent: '📱',
  email_sent: '📨'
};

// Entity type labels (UI)
export const TIMELINE_ENTITY_TYPE_LABELS: Record<TimelineEntityType, string> = {
  task: 'Tarefa',
  interaction: 'Atendimento',
  case: 'Caso',
  legal_process: 'Processo Judicial',
  document: 'Documento',
  note: 'Nota',
  payment: 'Pagamento',
  invoice: 'Fatura'
};

// Helper function to get route for entity
export function getEntityRoute(entityType: TimelineEntityType, entityId: string): string {
  const routes: Record<TimelineEntityType, string> = {
    task: `/tasks/${entityId}`,
    interaction: `/interactions/${entityId}`,
    case: `/cases/${entityId}`,
    legal_process: `/cases/${entityId}`,
    document: `/documents/${entityId}`,
    note: `/notes/${entityId}`,
    payment: `/finance/payments/${entityId}`,
    invoice: `/finance/invoices/${entityId}`
  };

  return routes[entityType] || '#';
}
